import React, { useEffect, useRef } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import { warnToast } from '@/utilities/toast.utilities';
import { SummaryHistogram } from '@/summaryReport/SummaryHistogram.organism';
import type { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import { SummaryReportBoxPlot } from '@/summaryReport/SummaryReportBoxPlot.organism';
import { useTranslation } from 'react-i18next';
import { SummaryReportTable } from '@/summaryReport/SummaryReportTable.organism';
import { SummaryReportToolbar } from '@/summaryReport/SummaryReportToolbar.organism';
import { sqSummaryReportStore, sqTrendConditionStore, sqTrendSeriesStore, sqWorksheetStore } from '@/core/core.stores';
import type { ItemSignalAndCondition } from '@/summaryReport/summaryReport.actions';
import { removeItems, setBaseline } from '@/summaryReport/summaryReport.actions';
import { NoStatisticsSummaryView } from '@/summaryReport/NoStatisticsSummaryView.atom';
import { ITEM_DATA_STATUS } from '@/trendData/trendData.constants';
import { isPresentationWorkbookMode } from '@/utilities/utilities';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { provideVisualizationData } from '@/annotation/ckEditorPlugins/components/content.utilities';
import { Visualization } from '@/annotation/ckEditorPlugins/components/content.utilities.constants';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import type { Y_AXIS_LABEL } from '@/summaryReport/summaryData.constants';
import { X_AXIS_LABEL } from '@/summaryReport/summaryData.constants';
import { SummaryReportTitleCard } from './SummaryReportTitleCard.organism';
import { useEqualHeights } from './useEqualHeights.hook';
import { loadToolForEdit } from '@/toolSelection/investigate.actions';
import { EDIT_MODE } from '@/main/app.constants';
import type { Timezone } from '@/datetime/timezone.service';
import { useItemSignalAndConditionMap } from '@/summaryReport/useItemSignalAndConditionMap.hook';
import { hasNoData, hasError } from '@/summaryReport/summaryReport.utilities';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';

export interface SummaryReportViewerProps {
  items: StatisticsSummaryItem[];
  isItemsOverlaid: boolean;
  status: ITEM_DATA_STATUS;
  showHistogram: boolean;
  showTable: boolean;
  showBoxPlot: boolean;
  visibleStats: string[];
  showBellCurve: boolean;
  isContent?: boolean;
  yAxisLabel: Y_AXIS_LABEL;
  xAxisLabel: X_AXIS_LABEL;
  baseline: StatisticsSummaryItem;
  showSignal: boolean;
  showTimeRange: boolean;
  showCondition: boolean;
  itemSignalAndConditionMap?: Record<string, ItemSignalAndCondition>;
  timezone?: Timezone;
  onItemEdit: (id: string) => void;
  onItemDuplicate: (id: string) => void;
  onItemClose: (item: StatisticsSummaryItem) => void;
  showBoxplotPoints: boolean;
}

export const SummaryReportViewerWrapper: React.FunctionComponent = () => {
  const {
    items,
    isItemsOverlaid,
    status,
    showHistogram,
    showTable,
    showBoxPlot,
    visibleStats,
    showBellCurve,
    yAxisLabel,
    xAxisLabel,
    baseline,
    showSignal,
    showTimeRange,
    showCondition,
    showBoxplotPoints,
  } = useFlux(sqSummaryReportStore);
  const timezone = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.timezone);
  const trendSignals = useFluxPath(sqTrendSeriesStore, () => sqTrendSeriesStore.items);
  const trendConditions = useFluxPath(sqTrendConditionStore, () => sqTrendConditionStore.items);

  const itemSignalAndConditionMap = useItemSignalAndConditionMap(items, trendSignals, trendConditions);

  useEffect(() => {
    if (!_.isEmpty(baseline) && !_.some(items, { id: baseline.id })) {
      setBaseline(null);
    }
  }, [items, baseline]);

  const props: SummaryReportViewerProps = {
    items,
    isItemsOverlaid,
    status,
    showHistogram,
    showTable,
    showBoxPlot,
    visibleStats,
    showBellCurve,
    yAxisLabel,
    xAxisLabel,
    baseline,
    showSignal,
    showTimeRange,
    showCondition,
    itemSignalAndConditionMap,
    timezone,
    onItemEdit: (id: string) => loadToolForEdit(id),
    onItemDuplicate: (id: string) => loadToolForEdit(id, EDIT_MODE.COPY),
    onItemClose: (item: StatisticsSummaryItem) => removeItems([item]),
    showBoxplotPoints,
  };

  return <SummaryReportViewer {...props} />;
};

export const SummaryReportViewer: React.FunctionComponent<SummaryReportViewerProps> = (props) => {
  const {
    items,
    isItemsOverlaid,
    status,
    isContent,
    showHistogram,
    showTable,
    showBoxPlot,
    visibleStats,
    showBellCurve,
    yAxisLabel,
    xAxisLabel,
    baseline,
    showSignal,
    showTimeRange,
    showCondition,
    itemSignalAndConditionMap = {},
    timezone,
    onItemEdit,
    onItemDuplicate,
    onItemClose,
    showBoxplotPoints,
  } = props;
  const isLoaded = status === ITEM_DATA_STATUS.PRESENT;
  const { t } = useTranslation();
  const isCompareMode = !_.isEmpty(baseline);
  const overlayInPairs = isItemsOverlaid && isCompareMode;
  const isPresentation = isPresentationWorkbookMode() || isContent;
  const containerRef = useRef<HTMLDivElement>(null);

  const titleRefsMap = React.useMemo(() => {
    const refsMap = new Map<string, React.RefObject<HTMLDivElement>>();
    items.forEach((item) => {
      const ref = React.createRef<HTMLDivElement>();
      if (ref !== null) {
        refsMap.set(item.id, ref as React.RefObject<HTMLDivElement>);
      }
    });
    return refsMap;
  }, [items]);

  const titleRefs = React.useMemo(() => Array.from(titleRefsMap.values()), [titleRefsMap]);

  const prevItemIdsRef = useRef<string[]>([]);
  const prevOverlayInPairsRef = useRef<boolean>(overlayInPairs);
  const prevBaselineIdRef = useRef<string | null>(baseline ? baseline.id : null);

  useEffect(() => {
    if (!baseline) {
      return;
    }
    const currentItemIds = items.map((item) => item.id).sort();
    const prevItemIds = prevItemIdsRef.current;
    const addedItemIds = currentItemIds.filter((id) => !prevItemIds.includes(id));
    const uncomparableItems = items.filter((item) => item.data?.uom !== baseline.data?.uom && item.id !== baseline.id);

    // Show toast when a new uncomparable item is added
    if (addedItemIds.length > 0 && !_.isEmpty(baseline) && isLoaded && overlayInPairs) {
      const newlyUncomparableItems = items.filter(
        (item) => addedItemIds.includes(item.id) && item.data?.uom !== baseline.data?.uom && item.id !== baseline.id,
      );
      if (newlyUncomparableItems.length > 0) {
        warnToast({
          messageKey: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.UNIT_MISMATCH_WARNING',
          messageParams: {
            name: newlyUncomparableItems.map((item) => item.name).join(', '),
          },
        });
      }
    }

    // Show toast when overlayInPairs transitions from false to true
    const overlayJustActivated = !prevOverlayInPairsRef.current && overlayInPairs;
    const baselineChanged = overlayInPairs && prevBaselineIdRef.current !== baseline.id;
    const shouldShowToast =
      (overlayJustActivated || baselineChanged) && !_.isEmpty(baseline) && isLoaded && uncomparableItems.length > 0;

    if (shouldShowToast) {
      warnToast({
        messageKey: 'INVESTIGATE_TOOLS.SUMMARY_REPORT.UNIT_MISMATCH_WARNING_MULTI',
        messageParams: {
          names: uncomparableItems.map((item) => item.name).join(', '),
        },
      });
    }

    prevItemIdsRef.current = currentItemIds;
    prevOverlayInPairsRef.current = overlayInPairs;
    prevBaselineIdRef.current = baseline.id;
  }, [items, baseline, overlayInPairs, isLoaded]);

  // Equalize heights of summary report titles
  useEqualHeights(titleRefs, [items, isLoaded, showSignal, showTimeRange, showCondition, itemSignalAndConditionMap]);

  // Provide visualization data for interactive content
  if (headlessRenderMode()) {
    provideVisualizationData({
      visualization: Visualization.STATISTICS_SUMMARY,
      ...props,
    });
  }

  const getSharedSignalUnit = (items: StatisticsSummaryItem[]) => {
    const itemUnits = items.map((item) => (item.data?.uom ?? '').trim());
    if (itemUnits.some((unit) => unit.length === 0)) return undefined;

    const referenceUnit = itemUnits[0];
    const allUnitsMatch = itemUnits.every((unit) => unit === referenceUnit);
    return allUnitsMatch ? referenceUnit : undefined;
  };

  const renderStatisticsSummary = (stats: StatisticsSummaryItem | StatisticsSummaryItem[]) => {
    const items = Array.isArray(stats) ? stats : [stats];
    const itemsWithData = items.filter((item) => !hasNoData(item) && !hasError(item));
    const itemsHaveData = itemsWithData.length > 0;
    const sharedSignalUnit = getSharedSignalUnit(itemsWithData);
    const axisUnitsLabel = isItemsOverlaid ? sharedSignalUnit : itemsWithData[0]?.data?.uom;

    return (
      <div
        className={clsx(
          'flexRowContainer',
          'm5',
          'p10',
          'statsSummaryContent',
          'backgroundColorWhite',
          'height-fit-content',
          isItemsOverlaid ? 'min-width-700' : 'max-width-500',
        )}
        key={items.map((item) => item.id).join('_')}>
        <div className="flexColumnContainer flexWrap flexAlignStart flexAlignStretch flexRowGap10 mb10">
          {items.map((item) => (
            <SummaryReportTitleCard
              key={item.id}
              ref={titleRefsMap.get(item.id)}
              item={item}
              isPresentation={isPresentation}
              isOverlaid={isItemsOverlaid}
              signal={itemSignalAndConditionMap[item.id]?.signal}
              condition={itemSignalAndConditionMap[item.id]?.condition}
              timezone={timezone}
              showSignal={showSignal}
              showTimeRange={showTimeRange}
              showCondition={showCondition}
              onEdit={onItemEdit}
              onDuplicate={onItemDuplicate}
              onClose={onItemClose}
            />
          ))}
        </div>
        {itemsHaveData && (
          <>
            <div
              className={clsx(
                isItemsOverlaid ? 'flexColumnContainer' : 'flexRowContainer',
                'flexCenter flexSpaceBetween',
              )}>
              {showHistogram && (
                <div
                  className={clsx({
                    'width-50percent': isItemsOverlaid && showBoxPlot,
                    'width-90percent marginAuto': isItemsOverlaid && !showBoxPlot,
                    'width-maximum': !isItemsOverlaid,
                  })}>
                  <SummaryHistogram
                    items={itemsWithData}
                    title={t('INVESTIGATE_TOOLS.SUMMARY_REPORT.HISTOGRAM')}
                    xAxisTitle={xAxisLabel !== X_AXIS_LABEL.OFF ? axisUnitsLabel : undefined}
                    isOverlay={isItemsOverlaid}
                    showBellCurve={showBellCurve}
                    yAxisTitle={yAxisLabel}
                  />
                </div>
              )}
              {showBoxPlot && (
                <div
                  className={clsx(
                    isItemsOverlaid
                      ? { 'width-50percent': showHistogram, 'width-90percent marginAuto': !showHistogram }
                      : 'width-maximum',
                  )}>
                  <SummaryReportBoxPlot
                    items={itemsWithData}
                    title={t('INVESTIGATE_TOOLS.SUMMARY_REPORT.BOXPLOT')}
                    yAxisTitle={axisUnitsLabel}
                    isOverlay={isItemsOverlaid}
                    showLabels={showBoxplotPoints}
                  />
                </div>
              )}
            </div>
            {showTable && (
              <SummaryReportTable
                items={itemsWithData}
                visibleStats={visibleStats}
                isOverlay={isItemsOverlaid}
                isCompareMode={isCompareMode}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (_.isEmpty(items)) {
      return (
        <div className="flexRowContainer flexCenter flexFill">
          <NoStatisticsSummaryView displayText="INVESTIGATE_TOOLS.SUMMARY_REPORT.NO_STATISTICS_VIEW" />
        </div>
      );
    }

    const filteredItems = overlayInPairs
      ? items.filter((item) => item.data.uom === baseline.data.uom && item.id !== baseline.id)
      : items;

    if (isCompareMode && filteredItems.length === 0) {
      return (
        <div className="flexRowContainer flexCenter flexFill">
          <NoStatisticsSummaryView displayText="INVESTIGATE_TOOLS.SUMMARY_REPORT.NO_COMPARABLE_STATISTICS" />
        </div>
      );
    }

    if (overlayInPairs) {
      return filteredItems.map((item) =>
        baseline.data.uom === item.data.uom ? renderStatisticsSummary([baseline, item]) : renderStatisticsSummary(item),
      );
    }

    if (!isItemsOverlaid) {
      return items.map((item) => renderStatisticsSummary(item));
    }

    return renderStatisticsSummary(items);
  };

  const isLoading =
    (status === ITEM_DATA_STATUS.LOADING || status === ITEM_DATA_STATUS.INITIALIZING) && !_.isEmpty(items);
  const isFailure = status === ITEM_DATA_STATUS.FAILURE;
  const shouldShowContent = isLoaded || _.isEmpty(items);

  if (isLoading) {
    return (
      <div className="flexColumnContainer flexCenter flexFill height-maximum" data-testid="summaryReportLoading">
        <IconWithSpinner
          spinning={true}
          extraClassNames="fa-5x sq-text-primary"
          testId="summaryReportSpinner"
          correctSpin={false}
        />
      </div>
    );
  }

  if (isFailure) {
    return (
      <div className="flexColumnContainer flexCenter flexFill height-maximum" data-testid="summaryReportError">
        <span className="sq-text-danger">{t('INVESTIGATE_TOOLS.SUMMARY_REPORT.ERROR_LOADING_DATA')}</span>
      </div>
    );
  }

  return (
    <>
      {shouldShowContent && (
        <div className="resizablePanelContainer flexRowContainer" data-testid="summaryReportViewer">
          {!isPresentation && (
            <div className="flexColumnContainer flexSpaceBetween toolbar pl10">
              <SummaryReportToolbar />
            </div>
          )}
          <div
            ref={containerRef}
            className="flexColumnContainer height-maximum tw:overflow-auto backgroundColorOffWhite">
            {renderContent()}
          </div>
        </div>
      )}
    </>
  );
};
